import { ruleById, familyById, FAMILIES, CEILINGS } from './rules.js';
import type { Finding, RuleTally, ScoreSignal, SlopScore } from './types.js';

export interface Tier { id: string; min: number; name: string; remarks: string[] }
// Ordered from the cleanest result to the sloppiest; a score belongs to the last tier whose min it reaches.
// Remarks joke about what the page shows, never about who or what made it.
export const TIERS: Tier[] = [
  { id: 'handmade', min: 0, name: 'Handmade', remarks: [
    'This page is full of particulars that only make sense here. Our slop sensors had very little to hold on to.',
    'Our slop sensors found almost nothing to complain about, and frankly they are a little bored.',
    'Clean enough to eat off. Nothing on this page looks like it came free with a starter kit.',
  ] },
  { id: 'light', min: 15, name: 'A light drizzle', remarks: [
    'A few familiar moves crept in, but nobody would mistake this for a template on a first date.',
    'Mostly distinctive, with a light dusting of stock moves that a quick edit could sweep away.',
    'There is a whiff of autopilot in a couple of places, though most of the page sounds like itself.',
  ] },
  { id: 'medium', min: 35, name: 'Half-baked', remarks: [
    'This page is wearing a template\'s jacket, although it still has its own shoes.',
    'Somewhere in here is a real product. It is just standing behind a gradient.',
    'Equal parts particular and familiar, like a homemade cake with store-bought frosting.',
  ] },
  { id: 'heavy', min: 55, name: 'Heavily glazed', remarks: [
    'The page says a lot of confident things without saying much at all, like a pitch deck that learned HTML.',
    'We counted enough familiar patterns to start a small bingo club.',
    'If you squint, you can see the starter template smiling back at you.',
  ] },
  { id: 'certified', min: 75, name: 'Certified slop', remarks: [
    'This page unlocked the power of endless possibilities and misplaced the plot along the way.',
    'Swap the logo for another one and most visitors would never notice the difference.',
    'Every box on the template checklist is ticked, and a few of them are glowing.',
  ] },
];
export const tierFor = (value: number): Tier => [...TIERS].reverse().find(t => value >= t.min) ?? TIERS[0];
export function remarkFor(value: number, seed: string): string {
  const { remarks } = tierFor(value);
  let hash = 0; for (const ch of seed) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return remarks[hash % remarks.length];
}
// Families whose points count as slop evidence: they can support the supporting signals, and their count decides
// whether the heavier tiers are reachable. Proof claims count as evidence but still need interchangeable copy or
// structure before a score passes the heavy ceiling.
const PRIMARY = ['copy', 'surface', 'layout', 'unfinished', 'proof'];

// `tallies` carries each rule's full count. Reports saved before tallies existed are scored from their findings, which
// were capped at six per rule and view.
export function computeScore(findings: Finding[], tallies?: RuleTally[]): SlopScore {
  const source: RuleTally[] = tallies ?? findings.map(f => ({ ruleId: f.ruleId, viewport: f.viewport, count: 1 }));
  const views = new Map<string, Map<string, { count: number; light: boolean }>>();
  for (const t of source) {
    if (!ruleById(t.ruleId) || t.count <= 0) continue;
    const byView = views.get(t.ruleId) ?? new Map();
    const prior = byView.get(t.viewport);
    byView.set(t.viewport, { count: (prior?.count ?? 0) + t.count, light: (prior?.light ?? true) && !!t.light });
    views.set(t.ruleId, byView);
  }
  // Desktop and mobile usually repeat the same pattern, so the view worth more counts rather than both.
  let signals: ScoreSignal[] = [...views].map(([ruleId, byView]) => {
    const { weight } = ruleById(ruleId)!;
    const value = ({ count, light }: { count: number; light: boolean }) => light && weight.light !== undefined ? weight.light : Math.min(weight.cap, weight.base + weight.extra * (count - 1));
    const best = [...byView.values()].sort((a, b) => value(b) - value(a) || b.count - a.count)[0];
    const raw = value(best);
    return { ruleId, count: best.count, points: raw, raw, ...(best.light && weight.light !== undefined ? { light: true } : {}) };
  });
  // Supporting signals never establish a score on their own; they only add weight to an existing slop signal.
  if (!signals.some(s => { const rule = ruleById(s.ruleId)!; return rule.category === 'slop' && !rule.supporting; })) signals = signals.filter(s => !ruleById(s.ruleId)!.supporting);
  // Each family adds at most its cap, filled from its strongest signals; supporting signals also add no more than the
  // primary slop evidence they support.
  const order = (a: ScoreSignal, b: ScoreSignal) => b.raw! - a.raw! || a.ruleId.localeCompare(b.ruleId);
  let primary = 0;
  for (const family of FAMILIES) {
    let room = family.id === 'supporting' ? Math.min(family.cap, primary) : family.cap;
    for (const s of signals.filter(s => ruleById(s.ruleId)!.family === family.id).sort(order)) {
      s.points = Math.max(0, Math.min(s.raw!, room)); room -= s.points;
      if (PRIMARY.includes(family.id)) primary += s.points;
    }
  }
  signals = signals.map(s => s.points === s.raw ? (({ raw: _, ...rest }) => rest)(s) : s).sort((a, b) => b.points - a.points || a.ruleId.localeCompare(b.ruleId));
  let value = Math.min(100, signals.reduce((sum, s) => sum + s.points, 0));
  // Heavier tiers need independent families and evidence that copy or structure is interchangeable or unfinished.
  const families = new Set(signals.filter(s => s.points > 0).map(s => ruleById(s.ruleId)!.family).filter(f => PRIMARY.includes(f)));
  const has = (id: string, test: (s: ScoreSignal) => boolean = () => true) => signals.some(s => s.ruleId === id && s.points > 0 && test(s));
  const interchangeable = [has('template-text'), has('generic-copy', s => s.count >= 2 && !s.light), has('card-grid', s => !s.light), has('repeated-layouts'), has('template-hero')].filter(Boolean).length;
  let ceiling: SlopScore['ceiling'];
  if (value > CEILINGS.heavy && !(families.size >= 2 && interchangeable >= 1)) {
    ceiling = { value: CEILINGS.heavy, reason: `The signals add up to ${value}, but a score above ${CEILINGS.heavy} needs copy or structure that could belong to any product, or unfinished template content, alongside another independent family of signals.` };
  } else if (value > CEILINGS.certified && !(families.size >= 3 && (has('template-text', s => !s.light) || interchangeable >= 2))) {
    ceiling = { value: CEILINGS.certified, reason: `The signals add up to ${value}, but a score above ${CEILINGS.certified} needs three independent families of signals, including unfinished template content or two kinds of interchangeable copy or structure.` };
  }
  if (ceiling) value = ceiling.value;
  return { value, tierId: tierFor(value).id, signals, ...(ceiling ? { ceiling } : {}) };
}
export const familyName = (ruleId: string) => { const rule = ruleById(ruleId); return rule ? familyById(rule.family).name : ''; };
